import styled from "@emotion/styled"
import Link from "components/link/Link"
import Image from "components/media/ImageCLD"
import PopinButton from "components/popin-edito/PopinButton"
import { useConsole } from "contexts/Console"
import getMediaQuery from "css/breakpoints"
import { fullGrid } from "css/grid"
import { quote70, legend100 } from "css/text"
import { motion, useScroll, useTransform } from "framer-motion"
import { useRef } from "react"
import BeautyshotButton from "./BeautyshotButton"

const Root = styled(motion.section)`
  --offset: 4px;

  ${fullGrid}
  margin-block-start: -100vh;
  min-height: calc(100vh * var(--pos) + var(--offset));
  position: sticky;
  top: calc(var(--offset) / -2);

  * {
    pointer-events: none;
  }

  a,
  button {
    pointer-events: auto;
  }

  > div {
    position: relative;
  }
`

const Overlay = styled(motion.div)`
  background: rgb(0 0 0 / 0.5);
  grid-column: doc;
  grid-row: 1;
  pointer-events: none;
`

const Figure = styled.figure`
  grid-column: doc;
  grid-row: 1;
  height: calc(100vh + var(--offset));
  position: sticky;
  top: calc(var(--offset) / -2);

  picture,
  img {
    display: block;
    height: 100%;
    object-fit: cover;
  }
`

const Content = styled.div`
  color: white;
  display: flex;
  flex-direction: column;
  grid-column: main;
  grid-row: 1;
  margin-block-start: 200vh;
  padding-block: calc(var(--outer-margin) * 2);
  position: relative;
  row-gap: 2ex;
  text-wrap: pretty;
  z-index: 0;

  ${getMediaQuery("l")} {
    grid-column: ${({ text_end }) => (text_end ? "col 7 / span 5" : "col 1 / span 5")};

    // Depends on the asset: the text has to be visually on the same side as LTR
    [dir="rtl"] & {
      grid-column: ${({ text_end }) => (text_end ? "col 1 / span 5" : "col 7 / span 5")};
    }
  }

  > div > button,
  > div > a {
    display: inline-block !important;
    margin-block-start: 1.25rem;
  }

  /* a11y */
  html.prefers-contrast & div {
    color: black !important;
    position: relative;

    ::after {
      background: white;
      content: "";
      height: 100%;
      left: 50%;
      position: absolute;
      top: 50%;
      transform: translate(-50%, -50%);
      width: calc(100% + 20px);
      z-index: -1;
    }
  }
  .caption {
    margin-top: 20px;
    ${legend100}
  }
`

const Blockquote = styled.blockquote`
  ${quote70}
  margin: 0;
  p,
  ::before,
  ::after {
    display: inline;
  }

  ::before {
    content: var(--quote-opening);
  }

  ::after {
    content: var(--quote-closing);
  }
`

const CTA = styled(BeautyshotButton)`
  align-content: end;
  display: grid;
  grid-column: doc;
  grid-row: 1;
  height: calc(100dvh + var(--offset) - 2.5rem);
  justify-content: end;
  padding-bottom: 2.5rem;
  padding-inline-end: var(--outer-margin);
  position: sticky !important;
  top: calc(var(--offset) / -2);
`

function Text({ name, title, paragraph, text_end, cta = {} }) {
  return (
    <Content text_end={text_end}>
      <div>
        <Blockquote>
          <p dangerouslySetInnerHTML={{ __html: paragraph }} />
        </Blockquote>
        <p className='caption'>{title || name}</p>
        {cta.edito && <PopinButton ariaLabel={cta.label} reference={cta.edito} />}
        {cta.url && <Link href={cta.url}>{cta.label}</Link>}
      </div>
    </Content>
  )
}

export default function Quote({ content, name, text_end = false }) {
  const console = useConsole()
  const rroot = useRef()

  const { scrollYProgress } = useScroll({ target: rroot, offset: ["start start", "end start"] })
  const opacity = useTransform(scrollYProgress, [0, 0.5], [0, 1])

  const text = content.find(item => item.text)?.text
  const asset = content.find(item => item.asset)?.asset
  const beautyshot = content.find(item => item.beautyshot)?.beautyshot
  const cta = content.find(item => item.cta)?.cta

  console.log("TestimoneeQuote(%o)", { content, text_end, cta })

  return (
    <Root ref={rroot} className='dark-theme'>
      {asset && (
        <Figure key={asset._metadata?.uid}>
          <Image loading='lazy' sources={asset.asset} alt={asset.alt ?? ""} sizes='100vw' />
        </Figure>
      )}
      <Overlay style={{ opacity }} />
      {text && <Text key={text._metadata?.uid} {...text} name={name} text_end={text_end} cta={cta} />}
      {beautyshot && <CTA beautyshot={beautyshot} text_end={text_end} />}
    </Root>
  )
}
